import { createRevisionRefresh, type RealtimeStatus } from "@a2z/realtime";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { subscribeAuthenticatedRevisions } from "@a2z/realtime/firebase";
import { getFirebaseAuth, getFirebaseFirestore } from "@/lib/firebase/config";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import { queryKeys } from "@/lib/query/core/query-keys";

/**
 * The admin portal's one realtime status. The Firebase credential exchange is
 * owned by FcmBootstrap; it reports a failed or stalled exchange through
 * `reportCredentialError`, which shows "degraded" until the listener next
 * reports — the same contract as the seller's useSellerRealtime.
 */
export function useAccessRealtime(adminId?: string) {
  const client = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("connecting");
  const lifetime = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  useEffect(() => {
    if (!adminId || lifetime.changing) return;
    const auth = getFirebaseAuth();
    const db = getFirebaseFirestore();
    if (!auth || !db) {
      let active = true;
      queueMicrotask(() => { if (active) setStatus("unconfigured"); });
      if (import.meta.env.DEV) console.warn("Live updates are not configured for this portal.");
      return () => { active = false; };
    }
    const generation = lifetime.generation;
    const isCurrent = () => !getPrivateSessionState().changing && getPrivateSessionState().generation === generation;
    const refresh = createRevisionRefresh({ isCurrent, refresh: async () => {
      const queryKey = queryKeys.admin.session();
      const pending = client.getQueryCache().find({ queryKey, exact: true });
      if (pending?.state.fetchStatus === "fetching") await pending.promise?.catch(() => undefined);
      if (isCurrent()) await client.invalidateQueries({ queryKey, exact: true }, { cancelRefetch: false });
    } });
    const stop = subscribeAuthenticatedRevisions({
      auth, db, ownerId: adminId, ownerType: "ADMIN", isCurrent, onStatus: setStatus,
      sources: [{ path: "realtimeAdmin/global", topics: ["access"] }],
      onChange: refresh.enqueue,
    });
    return () => { refresh.dispose(); stop(); };
  }, [adminId, client, lifetime.changing, lifetime.generation]);
  // "unconfigured" is permanent for this build; a credential error cannot change it.
  const reportCredentialError = useCallback(() => setStatus(current => current === "unconfigured" ? current : "degraded"), []);
  return { status, reportCredentialError };
}
